<template>
  <page-outline :title="pageTitle">
    <template slot="pageContent">
      <b-row class="pb-3 no-gutters">
        <back-button :returnUrl="returnUrl"/>
      </b-row>
      <spinner v-if="activateSpinner"></spinner>
      <form-accordion v-if="!activateSpinner" :title="$t('administration.country')" :isTitleUpperCase="false">
        <b-row v-if="!hasId">
          <b-col sm="4" class="form-group">
            <form-write-group-label-input isMandatory
              inputType="listeSelect"
              :inputLabel="$t('administration.name')">
              <select autocomplete="off" :aria-label="$t('administration.name')" class="form-control h-30px mw-100 pr-4" id="sel4" v-model="selectedCountry">
                <option disabled value="">{{ $t('administration.countries.select_country') }}</option>
                <option v-for="(country, idx) in activableCountries" :key="'country-selector-' + idx"
                  :value="idx">{{ country }}</option>
              </select>
            </form-write-group-label-input>
          </b-col>
          <b-col sm="4" class="form-group">
            <span class="filter-item-title text-ellipsis">
              {{ $t('administration.countries.network') }}
            </span>
            <button
                id="norm-picker-link"
                type="button"
                class="filter-item-content d-flex justify-content-between p-1 pt-2 w-100"
                :aria-label="
                $t('administration.countries.network') + ' : ' +
                (normDisplay.label || $t('filter.selected_network_none'))
              ">

              <span :class="{ 'filter-item-input-complete': normDisplay.nb > 0 }">
                {{ normDisplay.label || $t('filter.selected_network_none') }}
              </span>

              <i
                  aria-hidden="true"
                  class="fa-light fa-chevron-down"
                  id="norm-chevron">
              </i>
            </button>
            <b-popover :target="'norm-picker-link'"
              :title="$t('filter.selected_network_none')"
              triggers="focus" placement="bottomright">
              <span v-for="(norm, index) in normList" :key="norm.name + '-' + index">
                <b-form-checkbox v-model="norm.state"
                  @change="getBICList()"
                  :disabled="norm.disabled"
                  :value="1"
                  :unchecked-value="0">
                  <span>{{ norm.label }}</span>
                </b-form-checkbox>
              </span>
            </b-popover>
          </b-col>
          <b-col sm="4" class="form-group">
          </b-col>
          <b-col sm="4" class="form-group">
            <form-write-group-label-input isMandatory
              inputType="listeSelect"
              :inputLabel="$t('administration.active')">
              <select autocomplete="off" :aria-label="$t('administration.active')" class="form-control h-30px mw-100 pr-4" v-model="active">
                <option value="2"> {{ $t('administration.no') }} </option>
                <option value="1"> {{ $t('administration.yes') }} </option>
              </select>
            </form-write-group-label-input>
          </b-col>
        </b-row>
        <b-row v-else>
          <b-col sm="6">
            <form-read-group-label-input :inputLabel="$t('administration.name')" readonly :inputValue="name">
            </form-read-group-label-input>
          </b-col>
          <b-col sm="6" v-if="!editMode">
            <form-read-group-label-input :inputLabel="$t('administration.countries.network')" readonly :inputValue="normDisplay.label">
            </form-read-group-label-input>
          </b-col>
          <b-col sm="4" class="form-group" v-else>
            <span class="filter-item-title text-ellipsis">
              {{ $t('administration.countries.network') }}
            </span>
            <button
                id="norm-picker-link"
                type="button"
                class="filter-item-content d-flex justify-content-between p-1 pt-2 w-100"
                :aria-label="
                $t('administration.countries.network') + ' : ' +
                (normDisplay.label || $t('filter.selected_network_none'))
              ">

              <span :class="{ 'filter-item-input-complete': normDisplay.nb > 0 }">
                {{ normDisplay.label || $t('filter.selected_network_none') }}
              </span>

              <i
                  aria-hidden="true"
                  class="fa-light fa-chevron-down"
                  id="norm-chevron">
              </i>
            </button>
            <b-popover :target="'norm-picker-link'"
              :title="$t('filter.selected_network_none')"
              triggers="focus" placement="bottomright">
              <span v-for="(norm, index) in normList" :key="norm.name + '-' + index">
                <b-form-checkbox v-model="norm.state"
                  @change="getBICList()"
                  :disabled="norm.disabled"
                  :value="1"
                  :unchecked-value="0">
                  <span>{{ norm.label }}</span>
                </b-form-checkbox>
              </span>
            </b-popover>
          </b-col>
          <b-col sm="2" v-if="editMode">
          </b-col>
          <b-col sm="4" class="form-group">
            <div v-if="!editMode">
              <span>{{ $t('administration.active') }}</span>
              <div class="inputParentFormReadGroup">
                <span v-if="active === '1'"><i class="fa-light fa-check btn btn-icon btn-icon-green px-0"/></span>
                <span v-else><i class="fa-light fa-times btn btn-icon btn-icon-red px-0"/></span>
              </div>
            </div>
            <form-write-group-label-input isMandatory v-else
              inputType="listeSelect"
              :inputLabel="$t('administration.active')">
              <select autocomplete="off" :aria-label="$t('administration.active')" class="form-control h-30px mw-100 pr-4" v-model="active">
                <option value="2"> {{ $t('administration.no') }} </option>
                <option value="1"> {{ $t('administration.yes') }} </option>
              </select>
            </form-write-group-label-input>
          </b-col>
        </b-row>
      </form-accordion>
      <form-accordion v-if="!activateSpinner" :title="$t('administration.countries.country_bic')" :isTitleUpperCase="false">
        <b-row>
          <b-col cols="4">
            <b-row
              v-if="orderedLocalBICList.length"
              class="mb-2 w-50">
              <b-col class="py-2">{{ $t('administration.countries.local_bic_list') }}</b-col>
            </b-row>
            <b-row
              v-if="orderedLocalBICList.length"
              class="mb-2">
              <b-col
                v-for="(localBIC) in orderedLocalBICList"
                :key="localBIC.idRTPBIC"
                cols="12"
                class="mb-2">
                <div class="d-flex align-items-center justify-content-between px-3 py-2 bg-light radio-list mr-0 border">
                  <b-col class="fw-bold">
                    <span>{{ localBIC.BIC }}</span>
                  </b-col>
                  <i v-if="!hasId || editMode"
                    class="fa fa-trash text-danger"
                    @click="deleteLocalBIC(localBIC.idRTPBIC, localBIC.BIC)" />
                </div>
              </b-col>
            </b-row>
            <span v-else>{{ $t('administration.countries.no_local_bic') }}</span>
          </b-col>
          <b-col cols="4">
            <spinner v-if="activateSpinnerBIC"></spinner>
            <b-row v-if="(!hasId || editMode) && !activateSpinnerBIC">
              <b-col cols="12" class="form-group mb-5">
                <form-write-group-label-input v-model="BIC"
                  :inputLabel="$t('administration.countries.bic_add')"
                  :placeholderValue="$t('administration.countries.bic_add')"
                  :regexpErrorIndicator="BICErrorFormat">
                  <b-button class="text-nowrap h-30px py-1" :disabled="!BIC" @click="addNewLocalBIC">{{ $t('common.add_label') }}</b-button>
                </form-write-group-label-input>
              </b-col>
            </b-row>
          </b-col>
          <b-col cols="4">
          </b-col>
        </b-row>
      </form-accordion>
      <b-row v-if="!activateSpinner" class="accordionForm mt-3 justify-content-end form-group p-0 pr-2 no-gutters">
        <div v-if="!hasId">
          <b-button variant="primary"
            @click="createCountry()">
            <i class="fa-light fa-save"></i>
            <span>{{ $t('common.sve') }}</span>
          </b-button>
        </div>
        <div v-else>
          <b-button v-if="!editMode" variant="primary"
            @click="editCountry()">
            <i class="fa-light fa-pen"></i>
            <span>{{ $t('administration.edit') }}</span>
          </b-button>
          <b-button v-else variant="primary"
            @click="saveCountry()">
            <i class="fa-light fa-pen"></i>
            <span>{{ $t('common.sve') }}</span>
          </b-button>
          <b-button variant="primary"
            @click="deleteCountry()">
            <i class="fa-light fa-trash"></i>
            <span>{{ $t('administration.del') }}</span>
          </b-button>
        </div>
      </b-row>
    </template>
  </page-outline>
</template>

<script>

import backButton from "@/components/common/backButton.vue"
import FormAccordion from '@/components/common/FormAccordion.vue'
import formReadGroupLabelInput from '@/components/common/formReadGroupLabelInput.vue'
import formWriteGroupLabelInput from '@/components/common/formWriteGroupLabelInput.vue'
import PageOutline from '@/components/common/PageOutline.vue'
import Spinner from '@/components/common/spinner.vue'

import _ from 'lodash'

import {errorMessageAlert} from '@/helpers/alert'
import {loadPage} from '@/helpers/routing'

export default {
  name: 'CountryForm',
  components: {
    'form-accordion': FormAccordion,
    'form-write-group-label-input': formWriteGroupLabelInput,
    'form-read-group-label-input': formReadGroupLabelInput,
    'page-outline': PageOutline,
    'back-button': backButton,
    'spinner': Spinner
  },
  props: ['id'],
  data () {
    return {
      activateSpinner: false,
      activateSpinnerBIC: false,
      active: 1,
      activeOrig: 1,
      BIC: '',
      BICErrorFormat: false,
      editMode: false,
      localBICList: [],
      name: '',
      normList: {
        normSRTP: {
          label: this.$i18n.t('common.norm.srtp'),
          id: '1',
          disabled: false,
          state: 0,
        },
        normHRTP: {
          label: this.$i18n.t('common.norm.giro'),
          id: '2',
          disabled: false,
          state: 0,
        },
        normPSD2: {
          label: this.$i18n.t('common.norm.psd2'),
          id: '3',
          disabled: true,
          state: 1,
        }
      },
      countryBICList: [],
      originalLocalBICList: [],
      originalSRTP: false,
      originalHRTP: false,
      originalPSD2: false,
      returnUrl: '/country/list/',
      selectedCountry: '',
      selectedCountryBIC: ''
    }
  },
  computed: {
    activableCountries() {
      const keysToRemove = new Set(this.$engine.administration.country.activeList.map(item => String(item.numericCode)))
      let result = Object.fromEntries(
        Object.entries(this.countryAvailableList)
        .filter(([key]) => !keysToRemove.has(String(key)))
        .sort(([, a], [, b]) => {
          return String(a).localeCompare(String(b));
        })
      )
      return result
    },
    countryAvailableList() {
      return {
        '004':'Afghanistan',
        '008':'Albania',
        '012':'Algeria',
        '020':'Andorra',
        '024':'Angola',
        '028':'Antigua and Barbuda',
        '032':'Argentina',
        '051':'Armenia',
        '036':'Australia',
        '040':'Austria',
        '031':'Azerbaijan',
        '044':'Bahamas',
        '048':'Bahrain',
        '050':'Bangladesh',
        '052':'Barbados',
        '112':'Belarus',
        '056':'Belgium',
        '084':'Belize',
        '204':'Benin',
        '064':'Bhutan',
        '068':'Bolivia',
        '070':'Bosnia and Herzegovina',
        '072':'Botswana',
        '076':'Brazil',
        '096':'Brunei Darussalam',
        '100':'Bulgaria',
        '854':'Burkina Faso',
        '108':'Burundi',
        '132':'Cabo Verde',
        '116':'Cambodia',
        '120':'Cameroon',
        '124':'Canada',
        '140':'Central African Republic',
        '148':'Chad',
        '152':'Chile',
        '156':'China',
        '170':'Colombia',
        '174':'Comoros',
        '180':'Congo',
        '178':'Congo',
        '188':'Costa Rica',
        '384':'Côte d\'Ivoire',
        '191':'Croatia',
        '192':'Cuba',
        '196':'Cyprus',
        '203':'Czechia',
        '208':'Denmark',
        '262':'Djibouti',
        '212':'Dominica',
        '214':'Dominican Republic',
        '218':'Ecuador',
        '818':'Egypt',
        '222':'El Salvador',
        '226':'Equatorial Guinea',
        '232':'Eritrea',
        '233':'Estonia',
        '748':'Eswatini',
        '231':'Ethiopia',
        '242':'Fiji',
        '246':'Finland',
        '250':'France',
        '266':'Gabon',
        '270':'Gambia',
        '268':'Georgia',
        '276':'Germany',
        '288':'Ghana',
        '300':'Greece',
        '308':'Grenada',
        '320':'Guatemala',
        '324':'Guinea',
        '624':'Guinea-Bissau',
        '328':'Guyana',
        '332':'Haiti',
        '340':'Honduras',
        '348':'Hungary',
        '352':'Iceland',
        '356':'India',
        '360':'Indonesia',
        '364':'Iran',
        '368':'Iraq',
        '372':'Ireland',
        '376':'Israel',
        '380':'Italy',
        '388':'Jamaica',
        '392':'Japan',
        '400':'Jordan',
        '398':'Kazakhstan',
        '404':'Kenya',
        '296':'Kiribati',
        '414':'Kuwait',
        '417':'Kyrgyzstan',
        '418':'Laos',
        '428':'Latvia',
        '422':'Lebanon',
        '426':'Lesotho',
        '430':'Liberia',
        '434':'Libya',
        '438':'Liechtenstein',
        '440':'Lithuania',
        '442':'Luxembourg',
        '450':'Madagascar',
        '454':'Malawi',
        '458':'Malaysia',
        '462':'Maldives',
        '466':'Mali',
        '470':'Malta',
        '584':'Marshall Islands',
        '478':'Mauritania',
        '480':'Mauritius',
        '484':'Mexico',
        '583':'Micronesia',
        '498':'Moldova',
        '492':'Monaco',
        '496':'Mongolia',
        '499':'Montenegro',
        '504':'Morocco',
        '508':'Mozambique',
        '104':'Myanmar',
        '516':'Namibia',
        '520':'Nauru',
        '524':'Nepal',
        '528':'Netherlands',
        '554':'New Zealand',
        '558':'Nicaragua',
        '562':'Niger',
        '566':'Nigeria',
        '408':'North Korea',
        '807':'North Macedonia',
        '578':'Norway',
        '512':'Oman',
        '586':'Pakistan',
        '585':'Palau',
        '591':'Panama',
        '598':'Papua New Guinea',
        '600':'Paraguay',
        '604':'Peru',
        '608':'Philippines',
        '616':'Poland',
        '620':'Portugal',
        '634':'Qatar',
        '642':'Romania',
        '643':'Russian Federation',
        '646':'Rwanda',
        '659':'Saint Kitts and Nevis',
        '662':'Saint Lucia',
        '670':'Saint Vincent and the Grenadines',
        '882':'Samoa',
        '674':'San Marino',
        '678':'Sao Tome and Principe',
        '682':'Saudi Arabia',
        '686':'Senegal',
        '688':'Serbia',
        '690':'Seychelles',
        '694':'Sierra Leone',
        '702':'Singapore',
        '703':'Slovakia',
        '705':'Slovenia',
        '090':'Solomon Islands',
        '706':'Somalia',
        '710':'South Africa',
        '410':'South Korea',
        '728':'South Sudan',
        '724':'Spain',
        '144':'Sri Lanka',
        '729':'Sudan',
        '740':'Suriname',
        '752':'Sweden',
        '756':'Switzerland',
        '760':'Syrian Arab Republic',
        '762':'Tajikistan',
        '834':'Tanzania',
        '764':'Thailand',
        '626':'Timor-Leste',
        '768':'Togo',
        '776':'Tonga',
        '780':'Trinidad and Tobago',
        '788':'Tunisia',
        '792':'Türkiye',
        '795':'Turkmenistan',
        '798':'Tuvalu',
        '800':'Uganda',
        '804':'Ukraine',
        '784':'United Arab Emirates',
        '826':'United Kingdom of Great Britain and Northern Ireland',
        '840':'United States of America',
        '858':'Uruguay',
        '860':'Uzbekistan',
        '548':'Vanuatu',
        '862':'Venezuela',
        '704':'Viet Nam',
        '887':'Yemen',
        '894':'Zambia',
        '716':'Zimbabwe'
      }
    },
    hasId () {
      return this.id !== '-1'
    },
    orderedCountryBICList () {
      return [...this.countryBICList].sort((a,b) => a.BIC.localeCompare(b.BIC))
    },
    orderedLocalBICList () {
      return [...this.localBICList].sort((a,b) => a.BIC.localeCompare(b.BIC))
    },
    pageTitle () {
      return this.id === '-1' ? this.$i18n.t('administration.countries.country_add') : this.$i18n.t('administration.countries.country_details')
    },
    realNormList () {
      let that = this
      const list = []
      for (const property in that.normList) {
        const norm = that.normList[property]
        list.push({
          tag: property,
          label: norm.label,
          state: norm.state,
          id: norm.id
        })
      }
      return list
    },
    normDisplay () {
      let realNormList = this.realNormList.filter(norm => norm.state === 1)
      if (realNormList.length === 0) {
        return {
          label: '',
          nb: 0
        }
      } else if (realNormList.length === 1) {
        return {
          label: realNormList[0].label,
          nb: 1
        }
      } else {
        return {
          label: realNormList.map(elem => elem.label).join(','),
          nb: realNormList.length
        }
      }
    }
  },
  watch: {
    selectedCountryBIC (newValue) {
      if (newValue) {
        let index = this.countryBICList.findIndex(item => item.idRTPBIC === newValue)
        let [item] = this.countryBICList.splice(index, 1)
        this.localBICList.push(item)
        this.selectedCountryBIC = ''
      }
    }
  },
  created () {
    if (this.id !== '-1') {
      this.normList.normPSD2.disabled = false
      this.name = this.$engine.administration.country.countryDetails.name
      this.active = this.$engine.administration.country.countryDetails.active
      this.activeOrig = this.$engine.administration.country.countryDetails.active
      if (this.$engine.administration.country.countryDetails.EBA) {
        this.normList.normSRTP.state = 1
        this.originalSRTP = true
      }
      if (this.$engine.administration.country.countryDetails.GIRO) {
        this.normList.normHRTP.state = 1
        this.originalHRTP = true
      }
      if (this.$engine.administration.country.countryDetails.PSD2) {
        this.normList.normPSD2.state = 1
        this.originalPSD2 = true
      } else {
        this.normList.normPSD2.state = 0
      }
      let that = this
      this.activateSpinner = true
      this.$engine.administration.country.fetch({idCountry: this.id})
      .then(resp => {
        this.activateSpinner = false
        that.localBICList = resp.data.countries[0].localBIC
        that.originalLocalBICList = _.cloneDeep(that.localBICList)
      })
      .catch(err => {
        this.activateSpinner = false
        errorMessageAlert(err, that)
      })
    } else {
      this.normList.normPSD2.disabled = true
      // this.getBICList()
    }
    this.$engine.administration.country.countryDetails = {}
  },
  methods: {
    addNewLocalBIC () {
      if (this.localBICList.filter(elem => elem.BIC === this.BIC).length) {
        this.BIC = ''
        return
      }
      let BICRegex = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/
      if (!BICRegex.test(this.BIC))
      {
        this.BICErrorFormat = true
        return
      }
      this.BICErrorFormat = false
      this.localBICList.push({BIC: this.BIC})
      this.BIC = ''
    },
    createCountry () {
      if (!this.selectedCountry) {
        this.okMessageBox(this.$t('errors.country_missing'))
        return
      }
      let that = this
      let norms = Object.values(this.normList)
        .filter(item => item.state === 1)
        .map(item => item.id)
        .join(',')

      let params = {
        numericCode: this.selectedCountry,
        active: this.active,
        norms: norms,
        localBIC: this.localBICList.map(elem => elem.idRTPBIC).join(','),
        newLocalBIC: this.localBICList.filter(elem => !elem.idRTPBIC).map(elem => elem.BIC).join(',')
      }
      this.$engine.administration.country.create(params)
      .then(() => {
        that.$engine.auth.refresh()
        .then(() => {
          that.$engine.auth.refresh()
          that.$bus.$emit('reset-webheader')
          that.okMessageBox(that.$t('administration.countries.country_added'))
          loadPage(that, {url: '/country/list/'})
        })
        .catch(err => {
          errorMessageAlert(err, that)
        })
      })
      .catch(err => {
        errorMessageAlert(err, that)
      })
    },
    deleteCountry () {
      let that = this
      this.$f7.modal({
        title: that.$t('administration.countries.country_deletetion_confirmation'),
        buttons: [
          {
            text: that.$t('common.cncl'),
            color: 'red',
            onClick: function () {
            }
          },
          {
            text: that.$t('common.yes_label'),
            onClick: function () {
              that.$engine.administration.country.delete(that.id)
              .then(() => {
                that.$engine.auth.refresh()
                .then(() => {
                  that.$engine.auth.refresh()
                  that.$bus.$emit('reset-webheader')
                  that.okMessageBox(that.$t('administration.countries.country_deleted'))
                  loadPage(that, {url: '/country/list/'})
                })
              })
              .catch(err => {
                errorMessageAlert(err, that)
              })
            }
          }
        ]
      })
    },
    deleteLocalBIC (idRTPBIC, BIC) {
      let index = this.localBICList.findIndex(elem => elem.idRTPBIC === idRTPBIC || elem.BIC === BIC)
      this.localBICList.splice(index, 1)
      // if (idRTPBIC) {
      //   this.countryBICList.push(item)
      // }
    },
    editCountry () {
      this.editMode = true
    },
    getBICList () {
      let that = this
      this.activateSpinnerBIC = true
      this.$engine.administration.country.bic.fetch({
        local: 0,
        networks: Object.values(this.normList)
          .filter(item => item.state === 1)
          .map(item => item.id)
      })
      .then(resp => {
        that.countryBICList = resp.data.countryBIC
        this.activateSpinnerBIC = false
      })
      .catch(err => {
        errorMessageAlert(err, that)
        this.activateSpinnerBIC = false
      })
    },
    okMessageBox (message) {
      this.$f7.modal({
        title: message,
        buttons: [
          {
            text: 'OK',
            onClick: function () {
              return
            }
          }
        ]
      })
    },
    saveCountry () {
      let addNorms = [];
      let removeNorms = [];
      let norms = [
        { id: 1, original: this.originalSRTP, current: !!this.normList.normSRTP.state },
        { id: 2, original: this.originalHRTP, current: !!this.normList.normHRTP.state },
        { id: 3, original: this.originalPSD2, current: !!this.normList.normPSD2.state },
      ];

      norms.forEach(({ id, original, current }) => {
        if (original !== current) {
          (current ? addNorms : removeNorms).push(id);
        }
      });

      let addLocalBIC = []
      let addNewLocalBIC = []
      let removeLocalBIC = []

      let origBIC = new Set(this.originalLocalBICList.map(elem => elem.BIC))
      let currentBIC = new Set(this.localBICList.map(elem => elem.BIC))

      addLocalBIC = this.localBICList.filter(elem => !origBIC.has(elem.BIC) && elem.idRTPBIC).map(elem => elem.idRTPBIC)
      addNewLocalBIC = this.localBICList.filter(elem => !origBIC.has(elem.BIC) && !elem.idRTPBIC).map(elem => elem.BIC)
      removeLocalBIC = this.originalLocalBICList.filter(elem => !currentBIC.has(elem.BIC)).map(elem => elem.idRTPBIC)

      if (this.active != this.activeOrig || addNorms.length || removeNorms.length || addLocalBIC.length || addNewLocalBIC.length || removeLocalBIC.length) {
        let that = this
        this.$engine.administration.country.edit({
          idCountry: this.id,
          addNorms: addNorms.join(','),
          active: this.active,
          removeNorms: removeNorms.join(','),
          addLocalBIC: addLocalBIC.join(','),
          addNewLocalBIC: addNewLocalBIC.join(','),
          removeLocalBIC: removeLocalBIC.join(',')
        })
        .then(() => {
          that.okMessageBox(that.$t('administration.countries.country_edited'))
          that.editMode = false
          loadPage(that, {url: '/country/list/'})
        })
        .catch(err => {
          errorMessageAlert(err, that)
        })
      } else {
        this.okMessageBox(this.$t('administration.countries.country_edited'))
        this.editMode = false
        loadPage(this, {url: '/country/list/'})
      }
    }
  }
}

</script>
